import { useState } from 'react';
import ModelProvider, { useModel } from '../ModelProvider';
import CheckLine from './PreflightFinding';
import { setEngineReady } from '../api';

/* ═══════════════════════════════════════════════════════════════════════════════
   GO LIVE PANEL — Session 9d (landings 1-3), + the post-go-live UX pass
   ─────────────────────────────────────────────────────────────────────
   ✅ ARMED. The server's GO_LIVE_ARMED is true (landing 3, 2026-08-10): pressing the
      button on a not-yet-live company WRITES is_engine_ready=true. The button says so.

   ★ THE PANEL MUST KNOW ABOUT *AFTER*. It was written for the pre-flip flow only, so
     once a company went live it still opened on "Run pre-flight check →" and offered a
     button labelled "preview — not yet armed" — two statements that were both false for
     that company. A screen whose signals describe the wrong state is the prepaid-editor
     lesson (CLAUDE.md §12), so an already-live company now gets a LIVE state that says
     when it went live and who flipped it, and the go-live action is not re-offered as
     the primary path. Display only — the endpoint and the flip logic are untouched.

   ★ PLAIN ENGLISH COMES FROM ONE PLACE. Every check's operator-facing name/meaning is
     read from CHECK_REGISTER via describeCheck() in engines/preflightCheck.js — beside
     the check definitions, so a new check cannot ship without its line. This component
     carries NO per-check strings of its own (Principle 0).

   ★ ONE PRE-FLIGHT, NOT TWO. This panel does NOT compute a verdict. It mounts a
     ModelProvider for the wizard's tenant and READS the verdict ModelProvider already
     produces (full P1–P11 against the INJECTED skeletons). That is the only place
     P9/P10 can be evaluated at all — they need computed bsData/isData. Writing a
     second pre-flight here would be the exact drift Principle 0 forbids.

   ★ WHY A NESTED PROVIDER. AdminPage sits OUTSIDE ModelProvider by design (App.js
     routes /admin separately), so there is no context to read. Mounting one scoped to
     this panel is the smallest correct answer — and it is LAZY: the operator presses
     "Run pre-flight check", so Step 9 does not pay a full model build on every visit.
   ═══════════════════════════════════════════════════════════════════════════════ */

const FONT = "'DM Sans',-apple-system,sans-serif";
const MONO = "'Space Mono',monospace";

const box = { border: '1px solid #E0E0E0', borderRadius: 6, padding: 16, fontFamily: FONT };

/* A full ISO timestamp (never a bare YYYY-MM-DD — the §5 rule is about date-ONLY strings,
   which parse at UTC midnight and roll back a day). Falls back to the raw value. */
function fmtStamp(iso) {
    if (!iso) return null;
    const d = new Date(iso);
    if (isNaN(d.getTime())) return String(iso);
    return d.toLocaleString(undefined, { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

/* The finding renderer (CheckLine) lives in ./PreflightFinding.jsx -- shared with the Admin Health Check tab (W-035),
   so the two cannot drift apart. Here it is called with no options: the onboarding fix, the raw offenders. */

function Verdict({ v }) {
    if (!v) return null;
    const armed = v.armed === true;
    return (
        <div style={{
            marginTop: 12, padding: 12, borderRadius: 6, fontSize: 12, fontFamily: FONT,
            background: armed ? '#E8F5E9' : '#EEF4FF',
            border: '1px solid ' + (armed ? '#A5D6A7' : '#C9DCFF'),
            color: armed ? '#1B5E20' : '#1A4DA0',
        }}>
            <div style={{ fontWeight: 700, marginBottom: 6 }}>
                {armed ? '✓ Flipped — this company is now live.' : 'Preview only — nothing was written.'}
            </div>
            <div style={{ whiteSpace: 'pre-wrap' }}>{v.message}</div>
            {v.verdict && (
                <details style={{ marginTop: 8 }}>
                    <summary style={{ cursor: 'pointer', fontWeight: 600 }}>The recorded evidence</summary>
                    <pre style={{ fontFamily: MONO, fontSize: 11, overflowX: 'auto', marginTop: 6 }}>
                        {JSON.stringify(v.verdict, null, 2)}
                    </pre>
                </details>
            )}
        </div>
    );
}

function GoLiveInner({ companyId, companyName, alreadyLive, onWentLive }) {
    const { preflight, loading, initialized } = useModel();
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);
    const [err, setErr] = useState('');

    if (loading || !initialized) {
        return <div style={{ ...box, color: '#999' }}>Building the model for <strong>{companyName}</strong> and running the pre-flight…</div>;
    }

    // ★ null is NOT a pass. An absent verdict means the check never ran — the gate must
    //   treat that as blocked, never as "no failures found" (§12.5: a green from a blind
    //   check is the worst possible go-live signal).
    const passed = preflight?.passed === true;
    const blocking = (preflight?.failures || []).filter(f => f.severity === 'BLOCKING');
    const warnings = (preflight?.failures || []).filter(f => f.severity !== 'BLOCKING');

    const run = async () => {
        setBusy(true); setErr(''); setResult(null);
        try {
            const res = await setEngineReady(companyId, {
                ready: true,
                preflight: {
                    passed: preflight.passed,
                    blocking: preflight.blockingCount,
                    // ★ THE RECORDED EVIDENCE IS NOT TRUNCATED SILENTLY. This payload is
                    //   what the server stores as "why did this go live?", and the first
                    //   version sliced it to 20 with no marker — offenders 21+ vanished
                    //   from the durable record while the screen said nothing. That is the
                    //   §12.5 reporting-layer class (a truncation can make a check lie),
                    //   introduced in landing 1 and closed here.
                    //   DISPLAY truncation is fine and stays (it is labelled "+N more");
                    //   EVIDENCE truncation is not. offenderCount is the REAL length, so a
                    //   reader can always tell whether the list is complete.
                    checks: (preflight.failures || []).map(f => ({
                        id: f.id, severity: f.severity, what: f.what,
                        offenderCount: (f.offenders || []).length,
                        offenders: f.offenders || [],
                    })),
                },
            });
            setResult(res.data);
            // W-027: a real flip changes is_engine_ready -- tell the shell to re-read its company list.
            if (res.data && res.data.armed === true && onWentLive) onWentLive();
        } catch (e) {
            const d = e.response?.data;
            setErr(d ? `${d.error}: ${d.message || ''}` + (d.fails ? '\n' + d.fails.map(f => `  • [${f.check}] ${f.reason}` + (f.offenders ? ` — ${f.offenders.join(', ')}` : '')).join('\n') : '') : e.message);
        } finally { setBusy(false); }
    };

    const reason = preflight == null
        ? 'The pre-flight has not produced a verdict for this company yet.'
        : preflight.errored
            ? `The pre-flight itself errored: ${preflight.error}. A check that threw is not a pass.`
            : `${blocking.length} blocking check${blocking.length === 1 ? '' : 's'} must clear first.`;

    return (
        <div style={box}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                <span style={{
                    background: passed ? '#E8F5E9' : '#FDECEA', color: passed ? '#1B5E20' : '#B71C1C',
                    borderRadius: 10, padding: '3px 10px', fontSize: 12, fontWeight: 700,
                }}>{passed ? '✓ Pre-flight passed' : '✗ Pre-flight blocked'}</span>
                <span style={{ fontSize: 12, color: '#666' }}>
                    {preflight ? `${preflight.blockingCount ?? '?'} blocking · ${preflight.warningCount ?? '?'} warning` : 'no verdict'}
                </span>
            </div>

            {/* ★ THE DISABLED STATE MUST SAY WHY. A greyed button with no reason is the
                "the signals describe the wrong thing" trap the prepaid editor taught us —
                so every blocking check is listed WITH ITS NAMED OFFENDERS, which
                preflightCheck already supplies. Never a bare count. */}
            {!passed && (
                <div style={{ background: '#FDECEA', border: '1px solid #F5C6C0', borderRadius: 6, padding: 12, fontSize: 12, color: '#B71C1C' }}>
                    <div style={{ fontWeight: 700, marginBottom: 6 }}>
                        {alreadyLive ? 'This company is live, but the check found problems' : `Cannot go live yet — ${reason}`}
                    </div>
                    {blocking.map((f, i) => <CheckLine key={i} f={f} tone="#7A1512" />)}
                </div>
            )}

            {!!warnings.length && (
                <div style={{ marginTop: passed ? 0 : 10, background: '#FFF8E1', border: '1px solid #FFE0A3', borderRadius: 6, padding: 10, fontSize: 12, color: '#8C6500' }}>
                    <div style={{ fontWeight: 700 }}>
                        {warnings.length} thing{warnings.length === 1 ? '' : 's'} worth knowing (none of them block going live)
                    </div>
                    {warnings.map((f, i) => <CheckLine key={i} f={f} tone="#7A5A00" />)}
                </div>
            )}

            {/* ★ ALREADY LIVE → the flip is NOT re-offered. Re-running the pre-flight on a live
                company is a legitimate diagnostic, but a big dark "Set X live" button under it
                would describe an action that has already happened. */}
            {alreadyLive ? (
                <div style={{ marginTop: 14, fontSize: 12, color: '#666' }}>
                    This company is already live — this was a diagnostic run and nothing was written.
                    {!passed && ' Fix the items above; the model is live with them.'}
                </div>
            ) : (
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 14 }}>
                    <button
                        onClick={run}
                        disabled={!passed || busy}
                        title={passed
                            ? `This writes is_engine_ready=true for ${companyName} — it goes live immediately. Reversible.`
                            : reason}
                        style={{
                            fontFamily: FONT, fontWeight: 700, fontSize: 13, padding: '9px 16px',
                            borderRadius: 6, border: 'none', cursor: (passed && !busy) ? 'pointer' : 'not-allowed',
                            background: '#1A1C2E', color: '#fff', opacity: (passed && !busy) ? 1 : 0.5,
                        }}
                    >
                        {busy ? 'Going live…' : `Set ${companyName || 'this company'} live`}
                    </button>
                    {/* ★ THE LABEL MUST NOT UNDERSTATE THE CONSEQUENCE. It said "preview — not yet
                        armed" for three months after the server was armed; a live button that
                        calls itself a preview is wrong in the dangerous direction. */}
                    <span style={{ fontSize: 11, color: '#888' }}>
                        This takes effect immediately. It changes no data and no number — only who can
                        see the company. Going back offline is not a button here: it is a super-admin action, done on request.
                    </span>
                </div>
            )}

            {err && <pre style={{ marginTop: 12, color: '#B71C1C', fontFamily: MONO, fontSize: 11, whiteSpace: 'pre-wrap' }}>{err}</pre>}
            <Verdict v={result} />
        </div>
    );
}

export default function GoLivePanel({ companyId, companyName, isEngineReady, engineReadyAt, engineReadyBy, onWentLive }) {
    const [open, setOpen] = useState(false);
    if (!companyId) return null;

    const live = isEngineReady === true;
    const when = fmtStamp(engineReadyAt);

    return (
        <div style={{ marginTop: 28 }}>
            <h3 style={{ fontFamily: FONT, color: '#1A1C2E', fontSize: 18, margin: '0 0 6px' }}>
                {live ? 'Live' : 'Go Live'}
            </h3>

            {live ? (
                /* ★ THE POST-GO-LIVE STATE. Before this, a live company re-opened on
                   "Run pre-flight check →" as though nothing had happened, and offered a
                   button to do the thing it had already done. The at/by columns are written
                   by the endpoint (server.js) and ride along on SELECT * FROM companies, so
                   this needs no new fetch. cid=1/2/3 went live by manual SQL long before the
                   button existed and carry NULL at/by — that case says so rather than
                   inventing a date. */
                <div style={{
                    border: '1px solid #A5D6A7', background: '#E8F5E9', borderRadius: 6,
                    padding: 14, fontFamily: FONT, color: '#1B5E20',
                }}>
                    <div style={{ fontSize: 15, fontWeight: 700 }}>&#10003; This model is live</div>
                    <div style={{ fontSize: 13, marginTop: 4 }}>
                        <strong>{companyName}</strong> is visible in the company switcher and usable by its own users.
                    </div>
                    <div style={{ fontSize: 12, marginTop: 6, color: '#2E6B31' }}>
                        {when
                            ? <>Went live {when}{engineReadyBy ? <> by <strong>{engineReadyBy}</strong></> : null}.</>
                            : <>Went live before the Go Live button existed, so no date was recorded.</>}
                    </div>
                    <div style={{ fontSize: 12, marginTop: 10, color: '#4A6B4C' }}>
                        Nothing further is required here. Taking a company back out of service is deliberately
                        not a button — it is done explicitly, on request.
                    </div>

                    {/* Secondary, and worded as a diagnostic so it cannot read as "still to do". */}
                    {!open && (
                        <button onClick={() => setOpen(true)} style={{
                            marginTop: 12, fontFamily: FONT, fontWeight: 600, fontSize: 12, padding: '6px 12px',
                            borderRadius: 6, border: '1px solid #A5D6A7', background: '#fff', color: '#1B5E20', cursor: 'pointer',
                        }}>
                            Re-run the pre-flight check (diagnostic)
                        </button>
                    )}
                </div>
            ) : (
                <p style={{ fontFamily: FONT, color: '#666', fontSize: 13, margin: '0 0 12px' }}>
                    Going live makes <strong>{companyName}</strong> visible in the company switcher and usable by its
                    own users. It changes no data and no number — <code>is_engine_ready</code> is read only by the
                    navigation. Going back offline is not a button: it is a super-admin action, done on request.
                </p>
            )}

            {!open ? (
                !live && (
                    <button onClick={() => setOpen(true)} style={{
                        fontFamily: FONT, fontWeight: 600, fontSize: 13, padding: '8px 14px', borderRadius: 6,
                        border: '1px solid #D1D5DB', background: '#fff', color: '#1A1C2E', cursor: 'pointer',
                    }}>
                        Run pre-flight check →
                    </button>
                )
            ) : (
                // key={companyId} so switching the wizard's tenant remounts cleanly, exactly as
                // App.js does for the main provider.
                <div style={{ marginTop: live ? 12 : 0 }}>
                    <ModelProvider companyId={companyId} key={companyId}>
                        <GoLiveInner companyId={companyId} companyName={companyName} alreadyLive={live} onWentLive={onWentLive} />
                    </ModelProvider>
                </div>
            )}
        </div>
    );
}
